import React, { useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Container, Box } from '@mantine/core';
import PlatformNavBar from '../../components/PlatformNavBar';

// Import Cisco components
import Options from "./Options";
import SimpleWebexAuth from "./SimpleWebexAuth";
import SpreadsheetProcessor from "./SpreadsheetProcessor";
import AsBuiltBuilder from "./AsBuiltBuilder";
import ToolsPage from "./ToolsPage";
import QueueMapping from "./tools/QueueMapping";
import WxCCFunctionGenerator from "./tools/FunctionCreator";
import UserListExport from "./tools/UserListExport";
import SQLQueryProcessor from "./SQLQueryProcessor";
import FlowCreator from "./FlowCreator";

// Import Footer component
import Footer from '../../components/Footer';

function CiscoApp({ onBackToPlatformSelector, platformInfo }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [authData, setAuthData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Available Options
  const options = {
    ddw: {
      name: "DDW Import",
      component: SimpleWebexAuth,
      description: "Import and process DDW files with smart token parsing",
      route: "/ddw-auth",
      templatePath: "/wxcc_files/wxcc_mock_ddd.xlsx",
      templateFileName: "Wxcc_DDW_Template.xlsx"
    },
    sqlProcessor: {
      name: "UCCE to WxCC Migration Tool",
      component: null,
      description: "Provide AWDB Queries to be ran to streamline the WxCC DDW data input using predefined template",
      route: "/sql-processor"
    },
    asBuilt: {
      name: "As-Built Builder",
      component: SimpleWebexAuth,
      description: "Build As-Built documentation from current configuration",
      route: "/as-built-auth"
    },
    tools: {
      name: "Tools",
      component: SimpleWebexAuth,
      description: "Access various utility tools with smart authentication",
      route: "/tools-auth"
    },
    flowCreator: {
      name: "Flow Creator",
      component: null,
      description: "Convert Lucidchart diagrams to WebEx Contact Center Flows",
      route: "/flow-creator"
    }
  };

  const handleOptionSelect = (techKey) => {
    console.log("Selected option:", techKey);
    setSelectedOption(techKey);
    navigate(options[techKey].route);
  };

  const handleBackToSelector = () => {
    console.log("Back to option selector");
    setSelectedOption(null);
    setAuthData(null); // Clear auth data when going back
    navigate('/');
  };

  const handleAuthSuccess = (tokenData, targetRoute) => {
    console.log("Authentication successful:", tokenData);
    setAuthData(tokenData);
    navigate(targetRoute);
  };

  // Function to handle template download
  const handleTemplateDownload = (filePath, fileName) => {
    const link = document.createElement('a');
    link.href = process.env.PUBLIC_URL + filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log(`Downloading template from: ${filePath}`);
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Container size="xl" style={{ flex: 1, padding: '20px' }}>
        {/* Top-left navigation: exit to platform selector + return to Cisco home */}
        <PlatformNavBar
          onBackToPlatformSelector={onBackToPlatformSelector}
          onBackToHome={() => navigate('/')}
          showHome={location.pathname !== '/'}
          homeLabel="Cisco home"
          color="blue"
          mb="xl"
        />

        {/* Cisco Platform Routes */}
        <Routes>
        {/* Home route - Option Selector */}
        <Route
          path="/"
          element={
            <Options
              options={options}
              onSelect={handleOptionSelect}
              onTemplateDownload={handleTemplateDownload}
            />
          }
        />

      {/* DDW Import Authentication route */}
      <Route
        path="/ddw-auth"
        element={
          <SimpleWebexAuth
            onSubmit={(tokenData) => handleAuthSuccess(tokenData, '/spreadsheet')}
          />
        }
      />



      {/* As-Built Builder Authentication route */}
      <Route
        path="/as-built-auth"
        element={
          <SimpleWebexAuth
            onSubmit={(tokenData) => handleAuthSuccess(tokenData, '/as-built')}
          />
        }
      />

      {/* Tools Authentication route */}
      <Route
        path="/tools-auth"
        element={
          <SimpleWebexAuth
            onSubmit={(tokenData) => handleAuthSuccess(tokenData, '/tools')}
          />
        }
      />

      {/* Spreadsheet route - only accessible after successful DDW auth */}
      <Route
        path="/spreadsheet"
        element={
          authData ? (
            <SpreadsheetProcessor
              onBackToOptionSelector={handleBackToSelector}
              authData={authData}
              soapCredentials={authData}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* SQL Query Processor route - no authentication required */}
      <Route
        path="/sql-processor"
        element={
          <SQLQueryProcessor
            onBackToTools={handleBackToSelector}
            soapCredentials={null}
            orgid={null}
          />
        }
      />

      {/* Flow Creator route - no authentication required */}
      <Route
        path="/flow-creator"
        element={
          <FlowCreator
            onBackToOptionSelector={handleBackToSelector}
            authData={authData} // Pass auth data if available
          />
        }
      />

      {/* As-Built Builder route - now directly accessible without auth */}
      <Route
        path="/as-built"
        element={
          <AsBuiltBuilder
            onBackToOptionSelector={handleBackToSelector}
            authData={authData} // authData will be null if not authenticated via another path
          />
        }
      />

      {/* New: Tools Page route - only accessible after successful Tools auth */}
      <Route
        path="/tools"
        element={
          authData ? (
            <ToolsPage
              onBackToOptionSelector={handleBackToSelector}
              authData={authData}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* New: Queue Mapping route - accessible from ToolsPage */}
      <Route
        path="/tools/queue-mapping"
        element={
          authData ? (
            <QueueMapping
              onBackToTools={() => navigate('/tools')} // Go back to ToolsPage
              authData={authData}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* NEW: WxCC Function Generator route - accessible from ToolsPage */}
      <Route
        path="/tools/wxcc-function-generator"
        element={
          authData ? (
            <WxCCFunctionGenerator
              onBackToTools={() => navigate('/tools')} // Go back to ToolsPage
              authData={authData}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* NEW: User List Export route - accessible from ToolsPage */}
      <Route
        path="/tools/user-list-export"
        element={
          authData ? (
            <UserListExport
              onBackToTools={() => navigate('/tools')} // Go back to ToolsPage
              authData={authData}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />


          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Container>

      {/* Footer with Cisco blue theme */}
      <Footer platformColor="blue" />
    </Box>
  );
}

export default CiscoApp;
